import { useMemo, useRef, useState } from 'react';

import { TimeFrameDto } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { addDays, addHours, addMonths, addYears, startOfDay, subDays, subHours, subMonths, subYears } from 'date-fns';

import {
  getFirstAndLastDayOfLastSixYears,
  getFirstAndLastDayOfLastSixMonths,
  getFirstAndLastHourOfLastTwentyFourHours,
  getFirstAndLastDayOfLastSixteenDays,
  getFirstAndLastFifteenMinOfLastFourHours,
  MONTHS_IN_PERIOD,
  YEARS_IN_PERIOD,
  DAYS_IN_PERIOD,
  HOURS_IN_PERIOD,
  HOURS_IN_FIFTEEN_MIN_PERIOD,
} from '../utils/dateUtils';

export interface TimeFrame {
  timeFrame: TimeFrameDto;
  startDate: string;
  endDate: string;
}
const timeFrameGetters = {
  Monthly: getFirstAndLastDayOfLastSixMonths,
  Yearly: getFirstAndLastDayOfLastSixYears,
  Daily: getFirstAndLastDayOfLastSixteenDays,
  Hourly: getFirstAndLastHourOfLastTwentyFourHours,
  FifteenMin: getFirstAndLastFifteenMinOfLastFourHours,
} as const;

export const useConsumptionGraphTimeFrame = () => {
  const [timeFrame, setTimeFrame] = useState<TimeFrameDto>('Monthly');
  const consumptionAbortControllerRef = useRef(new AbortController());

  const defaultStartDate = useMemo(() => {
    return timeFrameGetters[timeFrame]().startDate;
  }, [timeFrame]);
  const defaultEndDate = useMemo(() => {
    return timeFrameGetters[timeFrame]().endDate;
  }, [timeFrame]);
  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);

  const { date } = useFormatter();

  const handleTimeFrameChange = (timeFrame: TimeFrameDto) => {
    const timeFrameData = timeFrameGetters[timeFrame]();
    setTimeFrame(timeFrame);
    setStartDate(timeFrameData.startDate);
    setEndDate(timeFrameData.endDate);
  };

  const changePeriod = (direction: 'previous' | 'next') => {
    consumptionAbortControllerRef.current.abort();
    consumptionAbortControllerRef.current = new AbortController();

    const getBaseEndDate = (): Date => {
      if (direction === 'previous') {
        switch (timeFrame) {
          case 'Monthly':
            return subMonths(new Date(startDate), 1);
          case 'Yearly':
            return subYears(new Date(startDate), 1);
          case 'Daily':
            return subDays(new Date(startDate), 1);
          case 'Hourly':
            return subHours(new Date(startDate), 1);
          case 'FifteenMin':
            return new Date(startDate);
        }
      }

      // For 'next' direction
      switch (timeFrame) {
        case 'Monthly':
          return addMonths(new Date(endDate), MONTHS_IN_PERIOD);
        case 'Yearly':
          return addYears(new Date(endDate), YEARS_IN_PERIOD);
        case 'Daily':
          return addDays(new Date(endDate), DAYS_IN_PERIOD);
        case 'Hourly':
          return addHours(new Date(endDate), HOURS_IN_PERIOD);
        case 'FifteenMin':
          return addHours(new Date(endDate), HOURS_IN_FIFTEEN_MIN_PERIOD);
      }
    };

    const baseEndDate = getBaseEndDate();

    const newTimeFrameData = timeFrameGetters[timeFrame](baseEndDate);

    setStartDate(newTimeFrameData?.startDate ?? '');
    setEndDate(newTimeFrameData?.endDate ?? '');
  };

  const getPeriodTitle = (timeFrame: string) => {
    switch (timeFrame) {
      case 'Monthly': {
        const yearFirstMonth = date.year(startDate) !== date.year(endDate) ? ` ${date.year(startDate)}` : '';
        return `${date.monthLong(startDate)}${yearFirstMonth} - ${date.monthLong(endDate)} ${date.year(endDate)}`;
      }
      case 'Yearly':
        return `${date.year(startDate)} - ${date.year(endDate)}`;
      case 'Daily': {
        const monthFirstDay =
          date.monthLong(startDate) !== date.monthLong(endDate) ? ` ${date.monthLong(startDate)}` : '';
        return `${date.dayOfMonthNumeric(startDate)}${monthFirstDay} - ${date.dayOfMonthNumeric(endDate)} ${date.monthLong(endDate)} ${date.year(endDate)}`;
      }
      case 'Hourly':
        return `${date.hourLong(startDate)} - ${date.hourLong(endDate)} ${date.dayOfMonthNumeric(endDate)} ${date.monthLong(endDate)} ${date.year(endDate)}`;
      case 'FifteenMin':
        return `${date.hourLong(startDate)} - ${date.hourLong(endDate)} ${date.dayOfMonthNumeric(endDate)} ${date.monthLong(endDate)} ${date.year(endDate)}`;
    }
  };

  const isDisabledNextPeriod = () => {
    const endOfCurrentPeriod = timeFrameGetters[timeFrame](new Date()).endDate;
    return startOfDay(new Date(endDate)) >= startOfDay(new Date(endOfCurrentPeriod));
  };

  return {
    timeFrame,
    setTimeFrame: handleTimeFrameChange,
    getPeriodTitle,
    changePeriod,
    startDate,
    endDate,
    isDisabledNextPeriod,
    consumptionFetchSignal: consumptionAbortControllerRef.current.signal,
  };
};
